import { Col, DatePicker, Input, Layout, Row } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { get, omit } from 'lodash';
import queryString from 'query-string';
import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import ResellerServices from 'services/reseller';

import StatusList from 'components/list-status';
import PdButton from 'components/pd-button';
import PdCsvDownloader from 'components/pd-csv-downloader';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';

import { errHandling, priceFormat } from 'utils';
import addDay from 'utils/addDay';

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Search } = Input;

export function OrderResellers() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(0);
  const [endDatePick, setEndDate] = useState('');
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [orderList, setOrderList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [startDatePick, setStartDate] = useState('');
  const [status, setStatus] = useState('');

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);
  const dateFormat = 'YYYY/MM/DD';

  const seeDetail = (row) => (
    <div className="button-table">
      <Link to={`/orders/detail?id=${row.id}`}>
        <PdButton type="primary" style={{ height: 32, width: 100 }}>
          See Detail
        </PdButton>
      </Link>
    </div>
  );

  const columns = [
    {
      name: 'Order No',
      selector: row => row.formatted_id,
      width: '18%',
    },
    {
      name: 'Date',
      selector: row => row.ordered_at,
      format: (row) => dayjs(row.ordered_at).format('DD/MM/YYYY'),
      width: '15%',
    },
    {
      name: 'Recipient',
      selector: row => row.user.full_name,
      width: '17%',
    },
    {
      name: 'Amount',
      selector: row => row.net_price,
      width: '18%',
      format: (row) => `IDR  ${priceFormat(row.net_price)}`,
    },
    {
      name: 'Status',
      selector: row => row.status,
      format: (row) => {
        let str = row.status.split('_');
        str = str.map((item) => {
          return item.charAt(0).toUpperCase() + item.slice(1);
        });
        return str.join(' ');
      },
      width: '12%',
    },
    {
      name: '',
      cell: seeDetail,
      center: true,
      width: '20%',
    },
  ];

  async function fetchOrderList({ endDate, page, q, startDate, status }) {
    setLoadStatus(true);
    try {
      let end = endDate;
      if (endDate) {
        end = dayjs(endDate, dateFormat).add(1, 'days');
        end = dayjs(end).format(dateFormat);
      }
      const { data } = await ResellerServices.getAllOrder({
        endDate: end,
        page,
        q,
        startDate,
        status,
      });
      const orderData = get(data, 'data', []);
      const cursor = get(data, 'metadata.next_cursor', -1);
      setOrderList(orderData);
      setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function handleDownloadCsv() {
    let newEndDate = parsed['end_date'];
    if (newEndDate) {
      newEndDate = addDay(parsed['end_date'], 1);
    }
    const { data } = await ResellerServices.getGlobalOrderReport({
      end_date: newEndDate,
      order_status: parsed['order_status'],
      q: parsed['q'],
      start_date: parsed['start_date'],
    });
    const orderData = get(data, 'data', []);
    return orderData
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_orders',
      search: stringified,
    });
  }

  function onChangeDate(dates, [startDate, endDate]) {
    if (startDate === '') delete parsed['start_date'];
    else parsed['start_date'] = startDate;

    if (endDate === '') delete parsed['end_date'];
    else parsed['end_date'] = endDate;

    parsed['page'] = 1;

    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_orders',
      search: stringified,
    });
  }

  function filterReseller(param, val) {
    if (!val) delete parsed[param];
    else parsed[param] = val;

    parsed['page'] = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/resellers_orders',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, ['end_date', 'limit', 'page', 'q', 'statuses', 'start_date'])
    );
    navigate({
      pathname: '/resellers_orders',
      search: stringified,
    });
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['q']) setSearchValue(parsed['q']);
    else setSearchValue('');

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/resellers_cashback',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['statuses']) setStatus(parsed['statuses']);
    else setStatus('');

    if (parsed['start_date']) setStartDate(parsed['start_date']);
    else setStartDate('');

    if (parsed['end_date']) setEndDate(parsed['end_date']);
    else setEndDate('');

    fetchOrderList({
      page: parsed['page'],
      q: parsed['q'],
      status: parsed['statuses'],
      startDate: parsed['start_date'],
      endDate: parsed['end_date'],
    });
  }, [
    parsed['end_date'],
    parsed['page'],
    parsed['q'],
    parsed['start_date'],
    parsed['statuses'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="main-container">
      <div className="content">
        <div className="pageTitle">Order Reseller</div>
        <Content className="pageContent">
          <Row gutter={18}>
            <Col span={14}>
              <Search
                value={searchValue}
                onChange={(event) =>
                  setSearchValue(get(event, 'target.value', event))
                }
                enterButton
                className="search-product"
                placeholder="Search Orders"
                onSearch={filterReseller.bind(this, 'q')}
              />
            </Col>
            {orderList.length > 0 ? (
              <Col span={4} offset={5} className="height-32">
                <PdCsvDownloader
                  datas={handleDownloadCsv}
                  filename={'order-data-homestory'}
             
                />
              </Col>
            ) : (
              <Col span={4} offset={5} className="height-32">
                <div className="invisible">invisible</div>
              </Col>
            )}
            <Col span={7} className="mt-1">
              <RangePicker
                className="w-full"
                format={dateFormat}
                onChange={onChangeDate}
                value={[
                  startDatePick && dayjs(startDatePick, dateFormat),
                  endDatePick && dayjs(endDatePick, dateFormat),
                ]}
              />
            </Col>
            <Col span={4} className="mt-1">
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>

            <Col span={24}>
              <Row gutter={10}>
                <Col span={3} className="mt-2">
                  <span style={{ marginRight: '10px' }}>Filter by Status:</span>
                </Col>
                <Col span={19} className="mt-1">
                  <Row>
                    <StatusList
                      onChange={(val) => filterReseller('statuses', val)}
                      selectedStatus={status}
                      statusList={[
                        '',
                        'waiting_payment',
                        'payment_verified',
                        'return_processed',
                        'shipped',
                        'delivered',
                        'cancelled',
                        'rejected',
                        'failed',
                        'completed',
                      ]}
                    />
                  </Row>
                </Col>
              </Row>
            </Col>

            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={orderList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { OrderResellers };
